import React from "react";
import Report from "../report/report";
import {Utils} from "../../../helpers/utils";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {IPaginationStore} from "../../../common/stores/paginationStore";
import {storesContext} from "../../../common/stores/stores";
import i18n from "../../../i18n/i18n";
import {ValidatedValue} from "../../../helpers/validatedValue";
import {DateUtils} from "../../../helpers/dateUtils";
import {VehicleOrCargoHandlingTimeModel, VehicleOrCargoHandlingTimeFilters} from "../../models/vehicleOrCargoHandlingTime";
import {VehicleOrCargoHandlingTimeService} from "../../services/vehicleOrCargoHandlingTimeService";
import TextInput from "../../../common/components/shared/textInput/textInput";
import Validator from "../../../helpers/validator";
import DateTimePicker from "../../../common/components/shared/dateTimePicker/dateTimePicker";

interface VehicleOrCargoHandlingTimeState {
    selectedBookingNo: ValidatedValue<string>;
    selectedLorryRegNo: ValidatedValue<string>;
    selectedTrailerRegNo: ValidatedValue<string>;
    selectedContainerNo: ValidatedValue<string>;
    selectedWagonRegNo: ValidatedValue<string>;
    selectedDateFrom: ValidatedValue<Date | null>;
    selectedDateTo: ValidatedValue<Date | null>;
    isSubmitButtonBlocked: boolean;
    isReportGenerated: boolean;
    generatedReport: VehicleOrCargoHandlingTimeModel[] | undefined | null;
    generatedReportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
}

export default class VehicleOrCargoHandlingTime extends React.Component <any, VehicleOrCargoHandlingTimeState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        const atLeastOneFilled = {
            name: "valuesAtLeastOneFilled",
            fn: () => Validator.atLeastOneFieldHasBeenCompleted([this.state.selectedBookingNo, this.state.selectedLorryRegNo, this.state.selectedContainerNo,
                this.state.selectedTrailerRegNo, this.state.selectedWagonRegNo]),
            errorMessage: () => `${i18n.t("errorMessages.atLeastOneFilled")}`
        };

        this.state = {
            selectedBookingNo: new ValidatedValue("", [atLeastOneFilled]),
            selectedLorryRegNo: new ValidatedValue("", [atLeastOneFilled]),
            selectedTrailerRegNo: new ValidatedValue("", [atLeastOneFilled]),
            selectedContainerNo: new ValidatedValue("", [atLeastOneFilled]),
            selectedWagonRegNo: new ValidatedValue("", [atLeastOneFilled]),
            isSubmitButtonBlocked: false,
            isReportGenerated: false,
            generatedReport: undefined,
            generatedReportResponse: undefined,
            currentPage: 0,
            selectedDateFrom: new ValidatedValue(null),
            selectedDateTo: new ValidatedValue(null)
        };
    }

    public render(): JSX.Element {
        const properties = ["bookingNo", "ferryName", "lorryRegNo", "trailerRegNo", "containerNo",
            "wagonRegNo", "location", "orderStatus", "createdAt", "timeBetweenEvents", "timeBetweenEventsCumulatively"];
        const headers = properties.map(key => i18n.t("reports.vehicleOrCargoHandlingTime.headers." + key));

        return (
            <Report
                className="vehicle-or-cargo-handling-time"
                filters={this.renderFilters()}
                onGenerateReport={this.onGenerateReport.bind(this)}
                generatedReport={this.state.generatedReport}
                generatedReportResponse={this.state.generatedReportResponse}
                isReportGenerated={this.state.isReportGenerated}
                reportTestId="vehicle-or-cargo-handling-time"
                reportProperties={properties}
                reportHeaders={headers}
                reportTableRenderCell={this.renderCell}
                currentPage={this.state.currentPage}
                reportService={VehicleOrCargoHandlingTimeService}
                isSummaryReportGenerated={false}
                selectedFilters={
                    {
                        bookingNo: this.state.selectedBookingNo.value,
                        lorryRegNo: this.state.selectedLorryRegNo.value,
                        trailerRegNo: this.state.selectedTrailerRegNo.value,
                        containerNo: this.state.selectedContainerNo.value,
                        wagonRegNo: this.state.selectedWagonRegNo.value,
                        dateFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateFrom.value),
                        dateTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateTo.value)
                    } as VehicleOrCargoHandlingTimeFilters
                }
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                summaryCurrentPage={0}
                summaryNumberOfElements={0}
            />
        );
    }

    private renderFilters(): JSX.Element {
        return (
            <>
                <TextInput
                    label={`${i18n.t("reports.vehicleOrCargoHandlingTime.filters.bookingNo")}`}
                    validatedValue={this.state.selectedBookingNo}
                    testId="filters-booking-no"
                    onChange={() => {
                        this.cleanValidators();
                    }}
                />
                <TextInput
                    label={`${i18n.t("reports.vehicleOrCargoHandlingTime.filters.lorryRegNo")}`}
                    validatedValue={this.state.selectedLorryRegNo}
                    testId="filters-lorry-reg-no"
                    onChange={() => {
                        this.cleanValidators();
                    }}
                />
                <TextInput
                    label={`${i18n.t("reports.vehicleOrCargoHandlingTime.filters.trailerRegNo")}`}
                    validatedValue={this.state.selectedTrailerRegNo}
                    testId="filters-trailer-reg-no"
                    onChange={() => {
                        this.cleanValidators();
                    }}
                />
                <TextInput
                    label={`${i18n.t("reports.vehicleOrCargoHandlingTime.filters.containerNo")}`}
                    validatedValue={this.state.selectedContainerNo}
                    testId="filters-container-no"
                    onChange={() => {
                        this.cleanValidators();
                    }}
                />
                <TextInput
                    label={`${i18n.t("reports.vehicleOrCargoHandlingTime.filters.wagonRegNo")}`}
                    validatedValue={this.state.selectedWagonRegNo}
                    testId="filters-wagon-reg-no"
                    onChange={() => {
                        this.cleanValidators();
                    }}
                />
                <DateTimePicker
                    label={i18n.t("reports.vehicleOrCargoHandlingTime.filters.dateFrom")}
                    validatedValue={this.state.selectedDateFrom}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDateFrom.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDateFrom, this.state.selectedDateTo);
                    }}
                    testId="filters-date-from"
                />
                <DateTimePicker
                    label={i18n.t("reports.vehicleOrCargoHandlingTime.filters.dateTo")}
                    validatedValue={this.state.selectedDateTo}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDateTo.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDateFrom, this.state.selectedDateTo);
                    }}
                    testId="filters-date-to"
                />
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "orderStatus":
                if (row[property]) {
                    return <>{i18n.t(`orderStatus.${row[property]}`)}</>;
                } else {
                    return <></>;
                }
            case "createdAt":
                return <>{DateUtils.getFormattedDateWithTime((row[property]))}</>;
            case "location":
                if (row[property]) {
                    return row[property];
                } else if(row["locationType"]) {
                    return <>{i18n.t(`reports.vehicleOrCargoHandlingTime.locationType.${row["locationType"]}`)}</>;
                } else {
                    return <></>;
                }
            default:
                return row[property];
        }
    }

    private get isValid(): boolean {
        this.state.selectedBookingNo.validate();
        this.state.selectedLorryRegNo.validate();
        this.state.selectedTrailerRegNo.validate();
        this.state.selectedContainerNo.validate();
        this.state.selectedWagonRegNo.validate();
        this.state.selectedDateFrom.validate();
        this.state.selectedDateTo.validate();

        return this.state.selectedBookingNo.isValid && this.state.selectedLorryRegNo.isValid
        && this.state.selectedTrailerRegNo.isValid && this.state.selectedContainerNo.isValid
        && this.state.selectedWagonRegNo.isValid && this.state.selectedDateFrom.isValid
        && this.state.selectedDateTo.isValid;
    }

    private async onGenerateReport() {
        this.setState({generatedReportResponse: undefined});
        const paginationStore = this.context.paginationStore as IPaginationStore;

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});

            const filters = {
                bookingNo: this.state.selectedBookingNo.value.trim(),
                lorryRegNo: this.state.selectedLorryRegNo.value.trim(),
                trailerRegNo: this.state.selectedTrailerRegNo.value.trim(),
                containerNo: this.state.selectedContainerNo.value.trim(),
                wagonRegNo: this.state.selectedWagonRegNo.value.trim(),
                dateFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateFrom.value),
                dateTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateTo.value)
            };

            const generatedReportResponse = await VehicleOrCargoHandlingTimeService.generateReport(
                filters
            );

            this.setState({
                isSubmitButtonBlocked: false,
                generatedReportResponse,
                generatedReport: generatedReportResponse?.data?.data || null,
                isReportGenerated: true,
                currentPage: generatedReportResponse?.data?.currentPage
            });

            paginationStore.setNumberOfElements(generatedReportResponse?.data?.numberOfElements);
            paginationStore.setNumberOfPages(generatedReportResponse?.data?.numberOfPages);
        }

        Utils.scrollToFirstErrorMessage();
    }

    private cleanValidators() {
        this.state.selectedBookingNo.clearValidation();
        this.state.selectedLorryRegNo.clearValidation();
        this.state.selectedTrailerRegNo.clearValidation();
        this.state.selectedContainerNo.clearValidation();
        this.state.selectedWagonRegNo.clearValidation();
    }
}